import { useAiSuggestStatusChecker } from '@/features/invoices/components/Details/Components/Context/AiSuggestStatus';
import { useOperationStatusChecker } from '@/features/invoices/components/Details/Components/Context/OperationStatus';
import { useTimestampStatusChecker } from '@/features/invoices/components/Details/Components/Context/TimestampStatus';
import { useGetFlags } from '@/hooks/useFeatureFlag';
import { useTranslation } from '@/i18n';
import {
  IconCaution,
  IconPending,
  InlineNotification,
} from '@moneyforward/ap-frontend-components';
import { FC, memo, ReactNode, useMemo } from 'react';

export type NotificationType = 'alert' | 'success';

export interface NotificationItem {
  message: string;
  type: NotificationType;
  icon: ReactNode;
}

// Custom hook to determine which notifications to display
const useActiveNotifications = () => {
  const { t } = useTranslation();
  const operationChecker = useOperationStatusChecker();
  const timestampChecker = useTimestampStatusChecker();
  const aiSuggestChecker = useAiSuggestStatusChecker();
  const flag = useGetFlags('E_DOC_METADATA');

  return useMemo(() => {
    const notifications: Array<NotificationItem> = [];

    // Archive notification
    if (operationChecker.isArchived) {
      notifications.push({
        message: t('invoice_archive_notification_message'),
        type: 'alert',
        icon: <IconCaution size={24} />,
      });
    }

    // Combined notification for OCR and Timestamp
    if (
      operationChecker.isOcrProcessing &&
      timestampChecker.isCreating &&
      flag
    ) {
      notifications.push({
        message: t('invoice_ocr_timestamp_notification_message'),
        type: 'success',
        icon: <IconPending size={18} />,
      });
    } else {
      // Individual notifications when not both active
      if (operationChecker.isOcrProcessing) {
        notifications.push({
          message: t('invoice_ocr_processing_notification_message'),
          type: 'success',
          icon: <IconPending size={18} />,
        });
      }

      // Timestamp notification (only shown if not combined with OCR)
      if (timestampChecker.isCreating && flag) {
        notifications.push({
          message: t('invoice_timestamp_creating_notification_message'),
          type: 'success',
          icon: <IconPending size={18} />,
        });
      }
    }

    // AI Suggest processing — placed behind the OCR and timestamp messages.
    if (aiSuggestChecker.isProcessing) {
      notifications.push({
        message: t('invoice_ai_suggest_processing_notification_message'),
        type: 'success',
        icon: <IconPending size={18} />,
      });
    }

    return notifications;
  }, [
    t,
    operationChecker.isArchived,
    operationChecker.isOcrProcessing,
    timestampChecker.isCreating,
    aiSuggestChecker.isProcessing,
    flag,
  ]);
};

export type Props = {};

export const Notification: FC<Props> = memo(() => {
  const activeNotifications = useActiveNotifications();

  if (activeNotifications.length === 0) {
    return null;
  }

  return (
    <>
      {activeNotifications.map((notification, index) => (
        // eslint-disable-next-line @moneyforward/account-payable/literals-in-props
        <div key={index} style={{ width: '100%' }}>
          <InlineNotification
            type={notification.type}
            visible
            message={notification.message}
            icon={notification.icon}
            visibleClose={false}
          />
        </div>
      ))}
    </>
  );
});

Notification.displayName = 'Notification';
